import { useUser } from "components/clerk";
import LocalDateTime from "components/general/LocalDateTime";
import { Box, Button, Divider, FormattedCurrency, H4, HStack, VStack, Text, View, Image, Spinner } from "components/ui";
import { H3, H6, Paragraph, ParagraphSmall, Typography } from "components/ui/Typography";
import { useLocalSearchParams, useNavigation, useSegments, useRouter } from "expo-router";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useGetOrderByDocumentNumberQuery, usePostOrderByDocumentNumberReorderMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import PopupModal from "components/general/PopupModal";
import { FlatList, Dimensions, Image as RNImage } from "react-native";

// Types
interface OrderSummaryRowProps {
	label: string;
	value: React.ReactNode;
	isMobile: boolean;
	isSubtotal?: boolean;
}

interface OrderDetailsProps {
	orderNumber: string | null;
	siteName?: string | null;
	transactionDate?: string;
}

interface PricingBreakdownProps {
	subTotal?: number;
	freightCharge?: number | null;
	taxTotal?: number;
	total?: number;
	isMobile: boolean;
}

interface PODItem {
	imageUrl?: string | null;
}

interface PODModalProps {
	isVisible: boolean;
	onClose: () => void;
	podCollection?: PODItem[] | null;
}

interface ActionButtonsProps {
	onReorder: () => void;
	onViewPOD: () => void;
	hasPOD: boolean;
	isMobile: boolean;
	isReordering: boolean;
	isLoadingPOD: boolean;
}

// Constants
const MODAL_CONFIG = {
	viewHeight: 800,
	animationIn: "fadeIn" as const,
	animationOut: "fadeOut" as const,
	scrollOffset: 1,
};

// Custom hooks
const useOrderActions = (documentNumber?: string | null) => {
	const router = useRouter();
	const [postReOrder, { isLoading: isReordering }] = usePostOrderByDocumentNumberReorderMutation();

	const handleReorder = useCallback(async () => {
		if (!documentNumber || isReordering) return;

		try {
			await postReOrder({ documentNumber }).unwrap();
			router.navigate("cart");
		} catch (error) {
			console.log("reorder:error", error);
		}
	}, [documentNumber, postReOrder, router, isReordering]);

	return { handleReorder, isReordering };
};

const useCurrentStep = () => {
	const segments = useSegments();
	const [currentStep, setCurrentStep] = useState<number>(1);

	const extractIdFromSegment = useCallback((segment: string) => {
		const match = segment.match(/\d+/);
		return match ? parseInt(match[0], 10) : 1;
	}, []);

	useEffect(() => {
		const part = segments[segments.length - 1];
		const step = extractIdFromSegment(part);
		setCurrentStep(step);
	}, [segments, extractIdFromSegment]);

	return currentStep;
};

// Component styles
const getContainerStyles = (isMobile: boolean) => ({
	borderBottomLeftRadius: isMobile ? 0 : 12,
	borderBottomRightRadius: isMobile ? 0 : 12,
	borderTopLeftRadius: 12,
	borderTopRightRadius: 12,
	flex: 1,
	maxWidth: isMobile ? "100%" : 302,
	height: "fit-content" as const,
	position: isMobile ? ("relative" as const) : ("sticky" as const),
	top: isMobile ? 0 : 100,
	bg: "$basicwhite",
	width: isMobile ? "100%" : 302,
	borderWidth: 1,
	overflow: "hidden",
	background: "$neutral50",
	borderColor: isMobile ? "$neutral100" : "$neutral25",
	borderTopWidth: isMobile ? 1 : 0,
	shadowColor: isMobile ? "$neutral200" : "transparent",
	shadowOffset: isMobile ? { width: 0, height: -2 } : { width: 0, height: 0 },
	shadowOpacity: isMobile ? 0.1 : 0,
	shadowRadius: isMobile ? 8 : 0,
	elevation: isMobile ? 4 : 0,
});

const getMobileBottomStyles = () => ({
	w: "$full" as const,
	borderWidth: 1,
	borderTopLeftRadius: 12,
	borderTopRightRadius: 12,
	borderColor: "black",
	paddingVertical: 24,
	paddingHorizontal: 16,
	gap: 16,
});

// Sub-components
const OrderSummaryRow: React.FC<OrderSummaryRowProps> = ({ label, value, isMobile, isSubtotal = false }) => (
	<HStack justifyContent="space-between">
		<Box>
			{isSubtotal ? (
				<H4 sx={{ fontSize: isMobile ? 16 : 14 }}>{label}</H4>
			) : (
				<ParagraphSmall sx={{ fontSize: isMobile ? 16 : 14 }} weight={isMobile ? "bold" : "normal"}>
					{label}
				</ParagraphSmall>
			)}
		</Box>
		{value}
	</HStack>
);

const OrderDetails: React.FC<OrderDetailsProps> = ({ orderNumber, siteName, transactionDate }) => (
	<Box gap={12} paddingHorizontal={4} paddingVertical={16} borderBottomWidth={1} borderColor="$neutral100">
		<ParagraphSmall weight="normal">
			Order number: <Typography weight="bold">{orderNumber || "N/A"}</Typography>
		</ParagraphSmall>
		<ParagraphSmall weight="normal">
			Site: <Typography weight="bold">{siteName || "N/A"}</Typography>
		</ParagraphSmall>
		<ParagraphSmall weight="normal">
			Order Date: <Typography weight="bold">{transactionDate ? <LocalDateTime utcDate={transactionDate} /> : "N/A"}</Typography>
		</ParagraphSmall>
	</Box>
);

const PricingBreakdown: React.FC<PricingBreakdownProps> = ({ subTotal, freightCharge, taxTotal, total, isMobile }) => (
	<>
		<Box paddingHorizontal={4} paddingVertical={16} borderBottomWidth={1} borderColor="$neutral100">
			<OrderSummaryRow label="Sub total:" value={<FormattedCurrency amount={subTotal ?? 0} component={H4} />} isMobile={isMobile} isSubtotal />
			<OrderSummaryRow label="Shipping:" value={<FormattedCurrency amount={freightCharge ?? 0} zeroIsFree={true} component={ParagraphSmall} />} isMobile={isMobile} />
			<OrderSummaryRow label="GST:" value={<FormattedCurrency amount={taxTotal ?? 0} component={ParagraphSmall} />} isMobile={isMobile} />
		</Box>
		<Box paddingHorizontal={4} paddingVertical={16}>
			<HStack justifyContent="space-between">
				<Box>
					<H6>Total:</H6>
					<ParagraphSmall weight="normal">Incl. GST</ParagraphSmall>
				</Box>
				<FormattedCurrency sx={{ fontSize: 28, lineHeight: 32 }} amount={total ?? 0} component={H3} />
			</HStack>
		</Box>
	</>
);

const PODModal: React.FC<PODModalProps> = ({ isVisible, onClose, podCollection }) => (
	<PopupModal {...MODAL_CONFIG} onBackdropPress={onClose} isVisible={isVisible} title="Proof of Delivery">
		<View style={{ flex: 1, justifyContent: "center", alignItems: "center", padding: 10 }}>
			{Array.isArray(podCollection) && podCollection.length > 0 ? (
				podCollection
					.filter((pod) => pod.imageUrl) // Filter out null/undefined imageUrls
					.map((pod, index) => (
						<RNImage
							key={index}
							source={{ uri: pod.imageUrl! }}
							style={{
								width: 400,
								height: 400 / 1.12,
								borderRadius: 10,
								marginBottom: 10,
							}}
							resizeMode="contain"
						/>
					))
			) : (
				<Typography sx={{ textAlign: "center" }} color="$darkgrey">
					No POD images available.
				</Typography>
			)}
		</View>
	</PopupModal>
);

const ActionButtons: React.FC<ActionButtonsProps> = ({ onReorder, onViewPOD, hasPOD, isMobile, isReordering, isLoadingPOD }) => (
	<HStack gap={isMobile ? 8 : 12} w="$full">
		<Button flex={1} variant="secondary" borderRadius={8} onPress={onReorder} disabled={isReordering} opacity={isReordering ? 0.6 : 1}>
			<HStack gap={8} alignItems="center">
				{isReordering && <Spinner size="small" color="black" />}
				<Typography.Paragraph>{isReordering ? "Reordering..." : "Reorder"}</Typography.Paragraph>
			</HStack>
		</Button>
		{hasPOD && (
			<Button flex={1} variant="outline" borderRadius={8} onPress={onViewPOD} disabled={isLoadingPOD} opacity={isLoadingPOD ? 0.6 : 1}>
				<HStack gap={8} alignItems="center">
					{isLoadingPOD && <Spinner size="small" />}
					<Typography.Paragraph sx={{ color: "$primarylight" }}>{isLoadingPOD ? "Loading..." : "View POD"}</Typography.Paragraph>
				</HStack>
			</Button>
		)}
	</HStack>
);

const MobileOrderSummary: React.FC<{
	order: any;
	onReorder: () => void;
	onViewPOD: () => void;
	hasPOD: boolean;
	isReordering: boolean;
	isLoadingPOD: boolean;
}> = ({ order, onReorder, onViewPOD, hasPOD, isReordering, isLoadingPOD }) => (
	<Box {...getMobileBottomStyles()}>
		<Box marginBottom={12}>
			<ParagraphSmall weight="normal">Order total:</ParagraphSmall>
			<FormattedCurrency sx={{ fontSize: 28, fontWeight: "bold" }} amount={order?.total} />
			<ParagraphSmall weight="normal">Excluding delivery charges & fees</ParagraphSmall>
		</Box>
		<ActionButtons onReorder={onReorder} onViewPOD={onViewPOD} hasPOD={hasPOD} isMobile={true} isReordering={isReordering} isLoadingPOD={isLoadingPOD} />
	</Box>
);

// Main component
const CartOrderPageSummary = () => {
	const { id } = useLocalSearchParams<{ id: string }>();
	const { data: order, isSuccess } = useGetOrderByDocumentNumberQuery({
		documentNumber: id,
	});

	const [isPodVisible, setIsPodVisible] = useState(false);
	const [isLoadingPOD, setIsLoadingPOD] = useState(false);
	const isMobile = useIsMobile();
	const currentStep = useCurrentStep();
	const { handleReorder, isReordering } = useOrderActions(order?.documentNumber);

	const hasPOD = useMemo(() => Boolean(order?.podCollection && order.podCollection.length > 0), [order?.podCollection]);

	const handleViewPOD = useCallback(async () => {
		if (isLoadingPOD) return;

		setIsLoadingPOD(true);
		// Add a small delay to show loading state (simulate network request if needed)
		await new Promise((resolve) => setTimeout(resolve, 200));
		setIsPodVisible(true);
		setIsLoadingPOD(false);
	}, [isLoadingPOD]);

	const handleClosePOD = useCallback(() => {
		setIsPodVisible(false);
	}, []);

	if (!order) {
		return null;
	}

	if (isMobile) {
		return (
			<>
				<MobileOrderSummary order={order} onReorder={handleReorder} onViewPOD={handleViewPOD} hasPOD={hasPOD} isReordering={isReordering} isLoadingPOD={isLoadingPOD} />
				<PODModal isVisible={isPodVisible} onClose={handleClosePOD} podCollection={order.podCollection} />
			</>
		);
	}

	return (
		<Box sx={getContainerStyles(isMobile)}>
			<Box borderTopLeftRadius={12} borderTopRightRadius={12} paddingHorizontal={16} paddingVertical={20} bg="$primary">
				<H4 sx={{ color: "$basicwhite" }}>Order summary</H4>
			</Box>

			<Box padding={12}>
				<OrderDetails orderNumber={order.documentNumber} siteName={order.siteName} transactionDate={order.transactionDateUtc} />

				<PricingBreakdown subTotal={order.subTotal} freightCharge={order.freightCharge} taxTotal={order.taxTotal} total={order.total} isMobile={isMobile} />

				<Box flex={1} flexDirection="row">
					<ActionButtons onReorder={handleReorder} onViewPOD={handleViewPOD} hasPOD={hasPOD} isMobile={isMobile} isReordering={isReordering} isLoadingPOD={isLoadingPOD} />
				</Box>
			</Box>

			<PODModal isVisible={isPodVisible} onClose={handleClosePOD} podCollection={order.podCollection} />
		</Box>
	);
};

export default CartOrderPageSummary;
